/* eslint-disable @typescript-eslint/no-duplicate-enum-values */

/**
 * @file
 *
 * Note: Separating certain types and interfaces into separate files is limited by
 *       the ts-json-schema-generator package which accepts only one file as its input.
 */

//#region Page Definitions

import { TFotoscapeCategory, TIABCategories, TPageFeedCategories } from 'types-generator/page-configuration/types'
import { IPlatform, IProduct } from 'types-generator/shared/interfaces'

// noinspection JSUnusedGlobalSymbols
/**
 * This represents the raw page data objects used to generate content.
 *
 * This validation definition currently works for output targets ['content', 'minusone', 'widget']
 */
export interface IPageConfiguration {
  /**
   * The title is used as the visible title element on the page.
   */
  title: string

  /**
   * This becomes a part of the URL structure and must be unique.
   */
  filenames: string[]

  /**
   * Choose the raw/template/views/* template that best suits your needs.
   */
  view: 'one_column' | 'one_column_full_page' | 'opt_in_campaign'

  /**
   * Choose either English, Portuguese, or Spanish (en|es|pt). The language is part of the URL structure.
   */
  language: TPageSettingLanguage

  /**
   * Select the output target of the current page.
   *
   * @default 'content'
   */
  outputTargets: TPageSettingOutputTarget[]

  /**
   * Explicit list of platforms this page will be generated for.  These are added to any platforms included
   * by platformsIncludeAllByProduct.
   */
  platforms?: IPlatform[]

  /**
   * Include all platforms by product type.
   */
  platformsIncludeAllByProduct?: IProduct[]

  /**
   * Explicit list of platforms to exclude from generation.
   */
  platformsExclude?: IPlatform[]

  /**
   * Block of page header.
   */
  headerBlocks?: TValidHeaderBlocks[]

  /**
   * Blocks of visible content on the page.
   */
  blocks: IValidBlock[]

  /**
   * Optional pubwise configuration to use.  If setting, must set a default.  Campaign configs must match platform definition.
   */
  campaigns?: IPageSettingAdCampaigns

  categories?: TPageFeedCategories[]

  google_search_bar?: PageSettingGoogleSearchBar

  iab_categories?: TIABCategories[]

  isBookmarksPage?: boolean

  /**
   * Optional notification settings to use for this page in the android client.
   */
  notification?: PageSettingNotification

  open_graph?: OpenGraph

  portal_header?: PageSettingPortalHeader

  portal_footer?: PageSettingPortalFooter

  section_navigation_bar?: PageSettingSectionNavigationBar
}

/**
 * Opt-in pages This represents the raw page data objects used to generate content.
 *
 * This validation definition currently works for output targets ['content', 'minusone', 'widget']
 */
// noinspection JSUnusedGlobalSymbols
export type IPageConfigurationOptIn =
  | IPageConfigurationOptInAllGreen
  | IPageConfigurationOptInMomentBasic
  | IPageConfigurationOptInMomentCarousel
  | IPageConfigurationOptInBasic

export interface IPageConfigurationOptInAllGreen extends IPageConfigurationOptInDefault {
  type: 'all-green'
}

export interface IPageConfigurationOptInMomentBasic extends IPageConfigurationOptInDefault {
  type: 'moment-basic'

  carrier_logo_dark: string
}

export interface IPageConfigurationOptInMomentCarousel extends IPageConfigurationOptInDefault {
  type: 'moment-carousel'
  // content for slider on optin-start page
  moment_start_items?: MomentStartItems[]

  //moment start page image
  moment_start_image?: string
}

export interface IPageConfigurationOptInBasic extends IPageConfigurationOptInDefault {
  type: 'basic'
}

export interface IPageConfigurationOptInDefault {
  /**
   * The title is used as the visible title element on the page.
   */
  title: string

  /**
   * This becomes a part of the URL structure and must be unique.
   */
  filenames: string[]

  /**
   * Choose the raw/template/views/* template that best suits your needs.
   */
  view: 'one_column' | 'opt_in_campaign'

  /**
   * Choose either English, Portuguese, or Spanish (en|es|pt). The language is part of the URL structure.
   */
  language: TPageSettingLanguage

  /**
   * Which platforms this block will show on (must be a subset of the page platforms array to render).
   */
  platforms: IPlatform[]

  /**
   * Select the output target of the current page.
   *
   * @default 'content'
   */
  outputTargets: TPageSettingOutputTarget[]

  /**
   * Adds a custom style class to the container.
   */
  custom_style?: string

  /**
   * Local path to carrier logo.
   */
  carrier_logo: string

  /**
   * Local path to hero image.
   */
  hero_image: string

  /**
   * Heading text, optional.
   */
  heading?: string

  /**
   * Subheading text / content.
   */
  subheading?: string

  /**
   * Header text, optional.
   */
  header_text?: string

  /**
   * Submit button text.
   */
  submit_text: string

  /**
   * Cancel button text, optional.
   */
  cancel_text?: string

  /**
   * Creates an 'a' tag for custom link on submit, optional.
   * Instead of the button.
   */
  custom_button_link?: string
}

export interface MomentStartItems {
  title: string
  subtitle: string
  paragraph: string
}

/**
 * Legal pages This represents the raw page data objects used to generate content.
 *
 * This validation definition currently works for output targets ['content', 'minusone', 'widget']
 */
// noinspection JSUnusedGlobalSymbols
export interface IPageConfigurationLegal {
  /**
   * The title is used as the visible title element on the page.
   */
  title: string

  partialName: string

  /**
   * This becomes a part of the URL structure and must be unique.
   */
  filenames: string[]

  /**
   * Choose the raw/template/views/* template that best suits your needs.
   */
  view: 'legal'

  /**
   * Choose either English, Portuguese, or Spanish (en|es|pt). The language is part of the URL structure.
   */
  language: TPageSettingLanguage

  /**
   * Which platforms this block will show on (must be a subset of the page platforms array to render).
   */
  platforms: IPlatform[]

  /**
   * Select the output target of the current page.
   *
   * @default 'content'
   */
  outputTargets: TPageSettingOutputTarget[]
}
//#endregion

//#region Top Level Page Settings

/**
 * Configures the section navigation bar on a page.
 */
export interface PageSettingSectionNavigationBar {
  display?: boolean

  /**
   * List of allowed platforms.  This is the same as platformsWhitelist (deprecated -- use platformsWhitelist instead).
   */
  platforms?: IPlatform[]

  /**
   * List of allowed platforms.  This is the same as platforms (use this and not platforms).
   */
  platformsWhitelist?: IPlatform[]

  /**
   * List of blocked platforms.
   */
  platformsBlacklist?: IPlatform[]
  /**
   * List of allowed product.
   */
  platformsIncludeAllByProduct?: IProduct[]
}

export interface PageSettingGoogleSearchBar {
  /**
   * Attribution value for monetization.  This should not be invalid or undefined in production.
   *
   * @todo This should be moved to platform_configuration (see #3584).
   */
  attribution: string

  /**
   * Toggles the display of the Google logo in the search bar.
   */
  show_google_logo: boolean

  /**
   * Toggles the display of the Google attribution next to search bar (Powered by Google).
   */
  show_google_attribution: boolean

  /**
   * Placeholder text for the search bar.
   */
  placeholder_text?: string
}

/**
 * Configures the portal header on a page.
 */
export interface PageSettingPortalHeader {
  items: BlockSettingFooterItem[]
}

export interface PageSettingPortalFooter {
  items: BlockSettingFooterItem[]
}

/**
 * Page language.  This setting feeds into the handlebars translation helper function.
 */
export type TPageSettingLanguage =
  | 'en'
  | 'es'
  | 'pt'
  | 'hi'
  | 'en-ae'
  | 'en-in'
  | 'en-it'
  | 'en-tn'
  | 'en-at'
  | 'es-mx'
  | 'de-at'
  | 'en-ph'
  | 'fr-ca'
  | 'it'

export interface OpenGraph {
  title: string
  description: string
}

/**
 * Output target types to generate.
 */
export enum TPageSettingOutputTarget {
  'content' = 'content',
  'minusone' = 'minusone',
  'widget' = 'widget'
}

export interface PageSettingNotification {
  description: string
  title: string
  imageUrl?: string
}

//#endregion

//#region Pubwise Campaign Settings

export interface IPageSettingAdCampaigns {
  /**
   * The default campaign.
   */
  default: IPubwiseCampaignModeFilter | IPubwiseCampaignSingle | IPubwiseCampaignSplit | IPubwiseCampaignUserAgent

  /**
   * Per platform custom campaigns, note, you must include a platforms list for these.
   */
  custom?: (IPubwiseCampaignModeFilter | IPubwiseCampaignSingle | IPubwiseCampaignSplit | IPubwiseCampaignUserAgent)[]
}

export interface IPubwiseCampaign {
  type: string

  /**
   * Limit this config to a specific list of platforms.  There is no duplicate platform checking.
   */
  platforms?: IPlatform[]
}

export interface IPubwiseCampaignModeFilter extends IPubwiseCampaign {
  type: 'mode'
  filter: {
    /**
     * Required.  This will be used when mode is unknown.
     */
    default: string

    // Optional.
    twa?: string
    cct?: string
    webview?: string
    unknown?: string
  }
}

export interface IPubwiseCampaignSingle extends IPubwiseCampaign {
  type: 'single'
  config: string
}

export interface IPubwiseCampaignSplit extends IPubwiseCampaign {
  type: 'split'
  control: string
  variant: string
  variantPercent: number
}

export interface IPubwiseCampaignUserAgent extends IPubwiseCampaign {
  type: 'useragent'
  matchList: string[]
  config: string
}

//#endregion

//#region Shared Settings

//#endregion

//#region Page Blocks

export interface IBlock {
  /**
   * Which platforms this block will show on (must be a subset of the page platforms array to render).
   */
  blockType: string

  /**
   * Include all platforms by product type.
   */
  platformsIncludeAllByProduct?: IProduct[]

  /**
   * Define an inclusion list of platforms.  A block will not be shown on platforms not included in this list if this
   * list is not empty.  Note that this setting is enabled for all block types.
   */
  platforms?: IPlatform[]

  /**
   * Define an exclusion list of platforms.  A block will not be shown on platforms included in this list if this
   * list is not empty.  Note that this setting is enabled for all block types.
   */
  excludePlatforms?: IPlatform[]

  /**
   * Optional parameter to help with reading folded blocks in code editors.
   */
  description?: string

  /**
   * Blocks of visible content on the page.
   */
  blocks?: IValidBlock[]

  settings?: any
}

export interface IBlockListSmall extends IBlock {
  blockType: 'list_small_block'
}

export interface IBlockAdvertisement extends IBlock {
  blockType: 'ad_unit'

  /**
   * Block settings.
   */
  settings?: {
    /**
     * Used for ad_unit. Must match ad configuration file for page/language/carrier.
     */
    adId?: string
    dimensions_override?: BlockSettingAdUnitDimension[]
    load_method?: string
    show_header?: boolean
    exclude_modes?: BlockSettingsExcludeMode[]
    dtx_enabled?: boolean
    dtx_placement_id?: number
    dtx_fallback_to_gam?: boolean
  }
}

export interface IBlockAdvertisementFloating extends IBlock {
  blockType: 'ad_unit_floating'

  /**
   * Block settings.
   */
  settings?: {
    /**
     * Used for ad_unit. Must match ad configuration file for page/language/carrier.
     */
    adId?: string
    dimensions_override?: BlockSettingAdUnitDimension[]
    load_method?: string
    show_header?: boolean
    exclude_modes?: BlockSettingsExcludeMode[]
    custom_method: boolean
  }
}

export interface IBlockAdviceOfDay extends IBlock {
  blockType: 'advice_of_day_block'
  settings?: {
    dynamic?: boolean
  }
}

export enum TBlockAppAdGridSettingsType {
  affiliate = 'affiliate',
  app = 'app',
  dynamic = 'dynamic'
}

export enum TBlockAppAdGridSettingsFallbackBehavior {
  static = 'static',
  collapse = 'collapse'
}

export interface IBlockAppAdGrid extends IBlock {
  blockType: 'app_ad_grid_block'

  /**
   * Block settings.
   */
  settings: {
    cachePerBlock?: boolean
    cacheTimeHours?: number
    count: BlockSettingCount
    countryCodeFromPlatform?: boolean
    fallbackBehavior?: TBlockAppAdGridSettingsFallbackBehavior
    title?: BlockSettingTitle
    type: TBlockAppAdGridSettingsType.app | TBlockAppAdGridSettingsType.dynamic
  }
}

export interface IBlockAffiliateAdGrid extends IBlock {
  blockType: 'affiliate_ad_grid_block'

  /**
   * Block settings.
   */
  settings: {
    count: BlockSettingCount
    title?: BlockSettingTitle
    type: TBlockAppAdGridSettingsType.affiliate
  }
}

export interface IBlockButtonNavigation extends IBlock {
  blockType: 'button_nav'

  /**
   * Block settings.
   */
  settings: {
    items: BlockSettingButtonNavigationItem[]
  }
}

export interface IBlockDiscoverBar extends IBlock {
  blockType: 'discover_bar'

  /**
   * Block settings.
   */
  settings?: {
    title?: BlockSettingTitle
  }
}

export interface IBlockFeedbackForm extends IBlock {
  blockType: 'feedback_form_block'
}

export interface IBlockFortuneCookie extends IBlock {
  blockType: 'fortune_cookie_block'
  settings?: {
    dynamic?: boolean
  }
}

export interface IBlockFotoscape extends IBlock {
  blockType: 'fotoscape_block'

  /**
   * Fotoscape block settings.
   */
  settings: {
    count: BlockSettingCount
    layout: BlockSettingLayoutFotoscapes
    cta?: boolean
    title?: BlockSettingTitle
    link_destination?: 'fotoscape' | 'full_page_article' | 'referral' | 'stub'
    link?: BlockSettingLink
    transition?: boolean
    layout_config?: {
      game_photocard?: {
        background_color: 'blue' | 'green' | 'purple'
      }
      stub?: {
        hide_description: boolean
      }
      trending_carousel?: {
        background_color: 'black' | 'blue' | 'green' | 'lightblue' | 'orange' | 'purple' | 'red' | 'teal'
      }
      daily_top?: {
        subtitle: string
        settings_icon_url: string
        button_title: string
        brought_by?: boolean
      },
      daily_top_minimal?: {
        subtitle: string,
        settings_icon_url: string
      }
    }

    /**
     * Used in fotoscape_block to select specific personalization schedule.
     */
    category?: TFotoscapeCategory
  }
}

export interface IBlockVideo extends IBlock {
  blockType: 'video_block'
}

export interface IBlockTrendingContent extends IBlock {
  blockType: 'trending_content',
  settings?: {
    category?: string;
  }
}

export interface IBlockFotoscapeStatic extends IBlock {
  blockType: 'fotoscape_static_block'

  settings: {
    category?: TFotoscapeCategory
    count: BlockSettingCount
    layout: BlockSettingLayoutFotoscapes
    title?: BlockSettingTitle
  }
}

export interface IBlockFotoscapeGallery extends IBlock {
  blockType: 'fotoscape_gallery_block'

  /**
   * Block settings.
   */
  settings: {
    title?: BlockSettingTitle
    category?: 'engage' | 'celebrity-homes' | 'outdoor_photography'
  }
}

export interface IBlockEmojiFeedback extends IBlock {
  blockType: 'emoji_feedback_block'
  settings: {
    title: string
  }
}

export interface IBlockExperiment extends IBlock {
  blockType: 'experiment_block'
  settings: {
    id: string | number
    variantPercent: number
    control:
      | IBlockAdvertisement
      | IBlockAdvertisementFloating
      | IBlockAdviceOfDay
      | IBlockFortuneCookie
      | IBlockFotoscape
      | IBlockHeader
      | IBlockHoroscope
      | IBlockHoroscopeTop
      | IBlockHoroscopeCarousel
      | IBlockHoroscopeDetails
      | IBlockHoroscopeDiscoverBar
      | IBlockHoroscopeGrid
      | IBlockIFrame
      | IBlockImageOnly
      | IBlockJokesWidgetStatic
      | IBlockMemesWidget
      | IBlockNativoBlock
      | IBlockOptInOnboarding
      | IBlockOptInLanding
      | IBlockOutbrain
      | IBlockOutbrainWidget
      | IBlockPlaybuzzVideo
      | IBlockPodcast
      | IBlockPromoCard
      | IBlockQuotesWidget
      | IBlockRevealWidget
      | IBlockStickyAdvertisement
      | IBlockAppAdGrid
      | IBlockWeatherWidget
    variant:
      | IBlockAdvertisement
      | IBlockAdviceOfDay
      | IBlockFortuneCookie
      | IBlockFotoscape
      | IBlockHeader
      | IBlockHoroscope
      | IBlockHoroscopeTop
      | IBlockHoroscopeCarousel
      | IBlockHoroscopeDetails
      | IBlockHoroscopeDiscoverBar
      | IBlockHoroscopeGrid
      | IBlockIFrame
      | IBlockImageOnly
      | IBlockJokesWidgetStatic
      | IBlockMemesWidget
      | IBlockNativoBlock
      | IBlockOutbrain
      | IBlockOutbrainWidget
      | IBlockPlaybuzzVideo
      | IBlockPodcast
      | IBlockPromoCard
      | IBlockQuotesWidget
      | IBlockRevealWidget
      | IBlockStickyAdvertisement
      | IBlockAppAdGrid
      | IBlockWeatherWidget
  }
}

export interface IBlockFullPageArticle extends IBlock {
  blockType: 'full_page_article'

  /**
   * Block settings.
   */
  settings: {
    type: 'fotoscape'
    layout: BlockSettingLayout
    title?: BlockSettingTitle
  }
}

export interface IBlockGoogleSearchBar extends IBlock {
  blockType: 'google_search_bar_block'
}

export interface IBlockHoroscope extends IBlock {
  blockType: 'horoscope_block'

  /**
   * Block settings.
   */
  settings: {
    type: BlockSettingHoroscopeType
    sign: BlockSettingHoroscopeSign
    title?: BlockSettingTitle
    leadsToChildPage?: boolean
  }
}

export interface IBlockHoroscopeGrid extends IBlock {
  blockType: 'horoscope_grid_block'

  /**
   * Block settings.
   */
  settings: {
    title?: BlockSettingTitle
    button?: {
      text: string
      link: string
    }
    leadsToChildPage?: boolean
  }
}

export interface IBlockHoroscopeTop extends IBlock {
  blockType: 'horoscope_top_block'

  /**
   * Block settings.
   */
  settings: {
    types: BlockSettingHoroscopeType[]
    sign: BlockSettingHoroscopeSign
    title?: BlockSettingTitle
  }
}

export interface IBlockHoroscopeCarousel extends IBlock {
  blockType: 'horoscope_carousel_block'

  /**
   * Block settings.
   */
  settings: {
    type: BlockSettingHoroscopeType
    title?: BlockSettingTitle
    title_moment?: BlockSettingTitle
    leadsToChildPage?: boolean
  }
}

export interface IBlockHoroscopeDetails extends IBlock {
  blockType: 'horoscope_details_block'

  /**
   * Block settings.
   */
  settings: {
    type: BlockSettingHoroscopeType
    sign: BlockSettingHoroscopeSign
  }
}

export interface IBlockHoroscopeDiscoverBar extends IBlock {
  blockType: 'horoscope_discover_bar'

  /**
   * Block settings.
   */
  settings?: {
    title?: BlockSettingTitle
    leadsToChildPage?: boolean
  }
}

export interface IBlockIFrame extends IBlock {
  blockType: 'iframe_block'
  settings: {
    url: string
  }
}

export interface IBlockImageOnly extends IBlock {
  blockType: 'image_only_block'
  settings: {
    /**
     * url of the uploaded image
     */
    image_url: string

    /**
     * destination url that the image links to
     */
    image_link: string

    /**
     * copy that is associated with the image so accessibility has meta information to read
     */
    image_alt_text: string
  }
}

export interface IBlockPromoCard extends IBlock {
  blockType: 'promo_card_block'

  /**
   * Block settings.
   */
  settings: {
    /**
     * The url for the promo link.
     */
    thumbnail_link: string

    /**
     * The image URL for the main thumbnail.
     *
     */
    thumbnail_url: string

    /**
     * The URL for the button link.
     */
    button_link: string

    /**
     * The text for the button.
     */
    button_text: string

    /**
     * Promo title.
     */
    title: string

    /**
     * Main promo text.
     */
    text: string
  }
}

// Designed to have multiple "reveal types", i.e. advice | jokes, etc.
export interface IBlockRevealWidget extends IBlock {
  blockType: 'reveal_widget_block'

  /**
   * Block settings.
   */
  settings: {
    revealType: 'words'
  }
}

export interface IBlockJokesWidgetStatic extends IBlock {
  blockType: 'jokes_widget_block'
}

export interface IBlockMemesWidget extends IBlock {
  blockType: 'memes_widget_block'
  settings?: {
    dynamic?: boolean
  }
}

export interface IBlockMultiplexBlock extends IBlock {
  blockType: 'multiplex_block'
}

export interface IBlockNativoBlock extends IBlock {
  blockType: 'nativo_block'

  /**
   * Block settings.
   */
  settings?: {
    layout: BlockSettingLayout
  }
}

export interface IBlockOptInOnboarding extends IBlock {
  blockType: 'opt_in_onboarding_block'

  /**
   * Block settings.
   */
  settings: {
    // custom style class
    custom_style?: string
    heading: string
    subheading: string
    image_on_light: string
    image_on_dark: string
    button_text: string
    custom_button_link?: string
    footer: {
      text: string
      cancel_text?: string
      agreement1_link: string
      agreement1_text: string
      agreement2_link?: string
      agreement2_text?: string
    }
  }
}

export interface IBlockOptInLanding extends IBlock {
  blockType: 'simple_landing_block'

  /**
   * Block settings.
   */
  settings: {
    title: string
    heading: string
    subheading: string
    image_on_light: string
    image_on_dark: string
    button_text: string
    button_destination_link: string
  }
}

export interface IBlockOutbrain extends IBlock {
  blockType: 'outbrain_block'

  /**
   * Block settings.
   */
  settings: {
    widgetId:
      | 'JS_1'
      | 'JS_2'
      | 'JS_3'
      | 'JS_4'
      | 'JS_5'
      | 'JS_6'
      | 'JS_9'
      | 'JS_12'
      | 'JS_16'
      | 'JS_13'
      | 'JS_14'
      | 'JS_17'
      | 'JS_21'
      | 'JS_22'
      | 'JS_23'
    layout: BlockSettingLayout

    /**
     * For sponsored content blocks. Show subtitle with 'sponsored by' text.
     */
    hasBranding?: boolean

    title?: BlockSettingTitle
    count?: BlockSettingCount
  }
}

export interface IBlockOutbrainWidget extends IBlock {
  blockType: 'outbrain_widget_block'

  /**
   * Block settings.
   */
  settings: {
    /**
     * For sponsored content blocks. Show subtitle with 'sponsored by' text.
     */
    hasBranding?: boolean

    title?: BlockSettingTitle
    widgetId: 'MB_5' | 'MB_6' | 'REELM_1' | 'MB_7' | 'BCR_1' | 'REELM_4' | 'AR_1' |
    'AR_2' | 'AR_3' | 'MB_1' | 'MB_2' | 'MB_3',
    permalink?: string
    psub?: string
  }
}

export interface IBlockPlaybuzzVideo extends IBlock {
  blockType: 'playbuzz_video_block'

  /**
   * Block settings.
   */
  settings?: {
    /**
     *  Render out the video block to either lazy load or not
     */
    lazyload?: boolean

    // These are added during process.
    is_valid?: boolean
    placement_id?: string
  }
}

export interface IBlockPodcast extends IBlock {
  blockType: 'podcast_block'

  settings?: {
    /**
     *  Small or Large block and title
     */
    type?: 'small' | 'large'
    title?: string
  }
}

export interface IBlockPortalFooter extends IBlock {
  blockType: 'portal_footer_block'
}

export interface IBlockPortalHeader extends IBlock {
  blockType: 'portal_header_block'
}

export interface IBlockQuotesWidget extends IBlock {
  blockType: 'quotes_widget_block'
}

export interface IBlockFeedItem extends IBlock {
  blockType: 'feed_item_block'

  /**
   * Block settings.
   */
  settings: {
    layout: BlockSettingLayout
    title?: BlockSettingTitle
    link_destination?: 'fotoscape' | 'full_page_article' | 'referral' | 'stub'
  }
}

export interface IBlockPortalNavigationBar extends IBlock {
  blockType: 'portal_navigation_bar'
}

export interface IBlockStickyAdvertisement extends IBlock {
  blockType: 'sticky_ad_unit'

  settings?: {
    exclude_modes?: BlockSettingsExcludeMode[]
  }
}

export interface IBlockWeatherWidget extends IBlock {
  blockType: 'weather_widget_block'
}

export interface IBlockTickerItem {
  platforms?: IPlatform[]

  /**
   * Explicit list of platforms to exclude from generation.
   */
  excludePlatforms?: IPlatform[]

  /**
   * Include all platforms by product type.
   */
  platformsIncludeAllByProduct?: IProduct[]
}

export interface IBlockTickerEndContent extends IBlockTickerItem {
  image?: string
  url?: string
  type: 'end_of_content'
}

export interface IBlockTickerImage extends IBlockTickerItem {
  image_url: string
  url: string
  type: 'image'
}

export interface IBlockTickerImageExternal extends IBlockTickerItem {
  image_url: string
  url: string
  type: 'image_external'
}

export interface IBlockTickerWeather extends IBlockTickerItem {
  type: 'weather'
}

export interface IBlockTickerFotoscapeLayout {
  font_alignment: 'right' | 'left'
  font_size: 'standard' | 'large'
}

export interface IBlockTickerFotoscapeImage extends IBlockTickerFotoscapeLayout {
  type: 'fotoscape_image'
}

export interface IBlockTickerFotoscapeTile extends IBlockTickerFotoscapeLayout {
  type: 'tile'
}

export interface IBlockTickerFotoscapeColor {
  type: 'color'
  font_size: 'standard' | 'large'
  background_color: string
}

export interface IBlockTickerFotoscape extends IBlockTickerItem {
  type: 'fotoscape'
  category: TFotoscapeCategory
  link_destination?: 'fotoscape' | 'full_page_article' | 'referral' | 'stub'
  layout: IBlockTickerFotoscapeTile | IBlockTickerFotoscapeImage | IBlockTickerFotoscapeColor
}

export type IBlockTickerItems =
  | IBlockTickerFotoscape
  | IBlockTickerImage
  | IBlockTickerImageExternal
  | IBlockTickerWeather
  | IBlockTickerEndContent

export interface IBlockTicker extends IBlock {
  blockType: 'ticker_block'
  settings: {
    autoplay?: boolean
    items: IBlockTickerItems[]
  }
}

export interface IBlockHeader extends IBlock {
  blockType: 'header_block'

  /**
   * Block settings.
   */
  settings?: {
    subheader: string
    isLarge?: boolean
    sponsoredAttribution?: string
  }
}

export interface IBlockHeaderAlert extends IBlock {
  blockType: 'header_block_alert'

  /**
   * Block settings.
   */
  settings?: {
    subheader: string
  }
}

export interface IBlockUpgradePrompt extends IBlock {
  blockType: 'upgrade_prompt'

  /**
   * Block settings.
   */
  settings: {
    confirm_text: string
    decline_text: string
    title_text: string
  }
}

export type IValidBlock =
  | IBlockAdvertisement
  | IBlockAdvertisementFloating
  | IBlockAdviceOfDay
  | IBlockAffiliateAdGrid
  | IBlockAppAdGrid
  | IBlockButtonNavigation
  | IBlockDiscoverBar
  | IBlockEmojiFeedback
  | IBlockExperiment
  | IBlockFeedbackForm
  | IBlockFortuneCookie
  | IBlockFotoscape
  | IBlockFotoscapeStatic
  | IBlockFotoscapeGallery
  | IBlockFullPageArticle
  | IBlockGoogleSearchBar
  | IBlockHeader
  | IBlockHeaderAlert
  | IBlockHoroscope
  | IBlockHoroscopeCarousel
  | IBlockHoroscopeDetails
  | IBlockHoroscopeDiscoverBar
  | IBlockHoroscopeGrid
  | IBlockHoroscopeTop
  | IBlockIFrame
  | IBlockImageOnly
  | IBlockJokesWidgetStatic
  | IBlockListSmall
  | IBlockMemesWidget
  | IBlockMultiplexBlock
  | IBlockNativoBlock
  | IBlockOptInLanding
  | IBlockOptInOnboarding
  | IBlockOutbrain
  | IBlockOutbrainWidget
  | IBlockPlaybuzzVideo
  | IBlockPodcast
  | IBlockPortalFooter
  | IBlockPortalHeader
  | IBlockPortalNavigationBar
  | IBlockPromoCard
  | IBlockQuotesWidget
  | IBlockRevealWidget
  | IBlockFeedItem
  | IBlockStickyAdvertisement
  | IBlockTicker
  | IBlockUpgradePrompt
  | IBlockWeatherWidget
  | IBlockVideo
  | IBlockTrendingContent

export type TValidHeaderBlocks =
  | IBlockGoogleSearchBar
  | IBlockPortalHeader
  | IBlockPortalNavigationBar
  | IBlockHeader
  | IBlockAdvertisement
  | IBlockButtonNavigation
  | IBlockTicker
//#endregion

//#region Block Level Settings

export type BlockSettingAdUnitDimension = [number, number]

/**
 * The number of items to show in a block.
 *
 * @default 1
 * @minimum 1
 */
export type BlockSettingCount = number

export type BlockSettingValidDayOfWeek =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'

export interface BlockSettingButtonNavigationItem {
  /**
   * Text to display
   */
  text: string

  /**
   * Link URL.
   */
  url: string

  /**
   * Additional CSS classes to apply to the navigation item.
   */
  additional_classes?: string
}

/**
 * Used in portal_header_block and portal_footer_block. An array of Text/Urls to show in the menu.
 */
export interface BlockSettingFooterItem {
  /**
   * The visible text in the footer item.
   */
  text: string

  /**
   * The url of the footer item.
   */
  url?: string
}

/**
 * Block header.  Used in BlockHouseDeal.
 */
export interface BlockSettingHeader {
  text: string
}

/**
 * Horoscope sign.
 */
export enum BlockSettingHoroscopeSign {
  'aries' = 'aries',
  'aquarius' = 'aquarius',
  'cancer' = 'cancer',
  'capricorn' = 'capricorn',
  'gemini' = 'gemini',
  'leo' = 'leo',
  'libra' = 'libra',
  'pisces' = 'pisces',
  'sagittarius' = 'sagittarius',
  'scorpio' = 'scorpio',
  'taurus' = 'taurus',
  'virgo' = 'virgo'
}

/**
 * Used in horoscope_block and horoscope_details_block.
 *
 * @default "general"
 */
export type BlockSettingHoroscopeType = 'general' | 'health' | 'love' | 'money'

export enum horoscopeSigns {
  aries = 'aries',
  aquarius = 'aquarius',
  cancer = 'cancer',
  capricorn = 'capricorn',
  gemini = 'gemini',
  leo = 'leo',
  libra = 'libra',
  pisces = 'pisces',
  sagittarius = 'sagittarius',
  scorpio = 'scorpio',
  taurus = 'taurus',
  virgo = 'virgo'
}

export interface HoroscopeSignsItem {
  sign: horoscopeSigns
  type?: BlockSettingHoroscopeType
}

export interface BlockSettingLink {
  text: string
  destination: string
}

/**
 * Which layout style to use for all feed items in this block.
 *
 * @default "edge"
 */
export type BlockSettingLayout =
  | 'carousel'
  | 'carousel-photocard'
  | 'daily-top'
  | 'daily-top-minimal'
  | 'deals-list'
  | 'edge'
  | 'edge-description'
  | 'edge-list'
  | 'edge-tile'
  | 'games-carousel'
  | 'games-hero'
  | 'games-tile'
  | 'large-photocard'
  | 'list-medium'
  | 'list-small'
  | 'list-small-sponsored'
  | 'podcast-large'
  | 'podcast-small'
  | 'quiz-carousel'
  | 'quiz-hero'
  | 'quiz-tile'
  | 'slideshow'
  | 'small-photocard'
  | 'small-photocard-flat'
  | 'stub'
  | 'tile'
  | 'trending-carousel'
  | 'instant-play-games'

/**
 * Which layout style to use for all feed items in this Fotoscapes block.
 *
 * @default "edge"
 */
export type BlockSettingLayoutFotoscapes =
  | 'carousel'
  | 'carousel-photocard'
  | 'daily-top'
  | 'daily-top-minimal'
  | 'deals-list'
  | 'edge'
  | 'edge-description'
  | 'edge-list'
  | 'edge-tile'
  | 'games-carousel'
  | 'games-hero'
  | 'games-photocard'
  | 'games-tile'
  | 'large-photocard'
  | 'list-medium'
  | 'list-small'
  | 'list-small-sponsored'
  | 'podcast-large'
  | 'podcast-small'
  | 'quiz-carousel'
  | 'quiz-hero'
  | 'quiz-tile'
  | 'slideshow'
  | 'small-photocard'
  | 'small-photocard-flat'
  | 'stub'
  | 'tile'
  | 'trending-carousel'
  | 'instant-play-games'

/**
 * Used in all regular blocks for main header.
 */
export interface BlockSettingTitle {
  /**
   * Text to show.
   */
  text: string
  isLarge?: boolean
  /**
   * Optional. Make header a link
   */
  url?: string
}

export enum BlockSettingsExcludeMode {
  webview = 'webview',
  twa = 'twa',
  cct = 'cct',
  unknown = 'unknown'
}

//#endregion
